/**
 * ResultsTab.jsx
 * Small, focused viewer for scheduled-inference results — reads what
 * inference_runner.service has already computed and stored (via
 * /schedules + /predictions on the inference wrap), independent of
 * InferenceTab's setup wizard. Unlike the old browser-tab-bound schedule
 * history, this survives closing the tab, switching devices, or reloading
 * the page — it's just reading persisted rows.
 */

import { useState, useEffect, useCallback } from "react";

// Schedules/predictions now live on the inference wrap, not the sensor API.
const API_BASE = "https://api.sensorplus.inference.gwc.dm-airtech.com";

const C = {
  bg:"#0f1117",surface:"#181c25",surfaceAlt:"#1e2330",border:"#2a3045",
  text:"#e8ecf4",textSub:"#7a8499",textMuted:"#4a5268",
  accent:"#4f8ef7",accentDim:"#1e3a6e",success:"#3ecf8e",successDim:"#0d3d28",
  warn:"#f5a623",danger:"#f05252",
};

const apiJson = async (path) => {
  const r = await fetch(`${API_BASE}${path}`, { headers: { "Content-Type": "application/json" } });
  if (!r.ok) throw new Error(r.statusText);
  return r.json();
};

const pill = (label, color=C.accent, bg=C.accentDim) => (
  <span style={{ fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:20, background:bg, color, letterSpacing:0.4 }}>{label}</span>
);
const Card = ({ children, style={} }) => (
  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"1rem 1.125rem", ...style }}>{children}</div>
);
const Label = ({ children }) => (
  <label style={{ fontSize:11, color:C.textSub, display:"block", marginBottom:4 }}>{children}</label>
);
const Spinner = () => (
  <span style={{ display:"inline-block", width:14, height:14, border:`2px solid ${C.border}`, borderTopColor:C.accent, borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
);
const Err = ({ msg }) => msg ? (
  <div style={{ background:"#3d1010", border:`1px solid ${C.danger}`, borderRadius:8, padding:"8px 12px", fontSize:12, color:C.danger, marginBottom:10 }}>{msg}</div>
) : null;

// ── Minimal dependency-free line chart (same shape as InferenceTab's) ─────────
function MiniLineChart({ seriesA, seriesB, labelA, labelB, colorA, colorB, height = 220 }) {
  const width = 640;
  const pad = 36;
  const all = [...(seriesA || []), ...(seriesB || [])].filter(v => v != null && !Number.isNaN(v));
  const min = all.length ? Math.min(...all) : 0;
  const max = all.length ? Math.max(...all) : 1;
  const span = max - min || 1;
  const n = Math.max((seriesA || []).length, (seriesB || []).length, 1);
  const x = i => pad + (i / Math.max(n - 1, 1)) * (width - pad * 2);
  const y = v => height - pad - ((v - min) / span) * (height - pad * 2);
  const toPath = (arr) => (arr || [])
    .map((v, i) => (v == null || Number.isNaN(v) ? null : `${i === 0 ? "M" : "L"}${x(i)},${y(v)}`))
    .filter(Boolean)
    .join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", display: "block" }}>
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke={C.border} />
      <line x1={pad} y1={pad} x2={pad} y2={height - pad} stroke={C.border} />
      {seriesB && <path d={toPath(seriesB)} stroke={colorB} strokeWidth={2} fill="none" strokeDasharray="5 4" />}
      <path d={toPath(seriesA)} stroke={colorA} strokeWidth={2} fill="none" />
      <circle cx={pad + 6} cy={pad - 2} r={3} fill={colorA} />
      <text x={pad + 14} y={pad + 1} fontSize={10} fill={colorA}>{labelA}</text>
      {seriesB && <>
        <circle cx={pad + 150} cy={pad - 2} r={3} fill={colorB} />
        <text x={pad + 158} y={pad + 1} fontSize={10} fill={colorB}>{labelB}</text>
      </>}
      <text x={4} y={height - pad + 4} fontSize={9} fill={C.textSub}>{min.toFixed(1)}</text>
      <text x={4} y={pad + 4} fontSize={9} fill={C.textSub}>{max.toFixed(1)}</text>
    </svg>
  );
}

export default function ResultsTab() {
  const [schedules, setSchedules]     = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [schedulesError, setSchedulesError]     = useState("");

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [predictions, setPredictions]       = useState([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [predictionsError, setPredictionsError]     = useState("");

  const [selectedRunIdx, setSelectedRunIdx] = useState(0);
  const [channel, setChannel] = useState(null);
  const [useNative, setUseNative] = useState(true);

  const loadSchedules = useCallback(async () => {
    setLoadingSchedules(true); setSchedulesError("");
    try {
      const r = await apiJson("/schedules");
      setSchedules(r.schedules || []);
      if (!selectedDevice && r.schedules?.length) {
        setSelectedDevice(r.schedules[0].device_id);
      }
    } catch (e) {
      setSchedulesError(e.message);
    } finally {
      setLoadingSchedules(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const loadPredictions = useCallback(async (deviceId) => {
    if (!deviceId) return;
    setLoadingPredictions(true); setPredictionsError(""); setSelectedRunIdx(0);
    try {
      const r = await apiJson(`/predictions?device_id=${encodeURIComponent(deviceId)}&limit=50`);
      setPredictions(r.data || []);
      const first = r.data?.[0];
      // Seed the channel picker with a channel that's actually comparable
      // against a baseline (i.e. present in both channels_json and
      // baseline_json.channels) rather than just channels_json[0] — since
      // channels_json can now include sensor-side channels (tagged
      // "<name>__sensor") that have no NWP baseline to plot against. See
      // the same reasoning below where comparableChannels is computed for
      // the currently-selected run.
      const firstChannels = first?.channels_json || [];
      const firstBaselineChannels = first?.baseline_json?.channels || [];
      const firstComparable = firstChannels.filter(c => firstBaselineChannels.includes(c));
      if (firstComparable.length) {
        setChannel(firstComparable[0]);
      } else if (firstChannels.length) {
        setChannel(firstChannels[0]);
      }
    } catch (e) {
      setPredictionsError(e.message);
    } finally {
      setLoadingPredictions(false);
    }
  }, []);

  useEffect(() => { if (selectedDevice) loadPredictions(selectedDevice); }, [selectedDevice, loadPredictions]);

  const run = predictions[selectedRunIdx];
  const channels = run?.channels_json || [];
  const baselineChannels = run?.baseline_json?.channels || [];
  // Only offer channels that exist in BOTH predictions and the baseline —
  // channels_json can include sensor-side channels (predictions_json rows
  // fed straight from the sensor, tagged "<name>__sensor" by
  // inference_server.py) that the raw NWP baseline never has, since the
  // baseline is drawn purely from the forecast product itself. Same
  // reasoning as InferenceTab.jsx's postprocessing dashboard. Falls back to
  // the full channel list for older stored runs whose baseline_json has no
  // "channels" key at all (pre-fix rows) so the picker still shows
  // something rather than going empty.
  const comparableChannels = baselineChannels.length
    ? channels.filter(c => baselineChannels.includes(c))
    : channels;
  const chan = channel && comparableChannels.includes(channel) ? channel : comparableChannels[0];

  // Two SEPARATE indices — channels_json and baseline_json.channels are
  // independent lists (predictions can be longer, sensor+forecast, than
  // baseline, forecast-only) — never assume they share a position.
  const chIdx = channels.indexOf(chan);
  const baselineChIdx = baselineChannels.indexOf(chan);

  const nativeAvailable = !!run?.predictions_native_json;
  const modelSeries = (useNative && nativeAvailable)
    ? (run.predictions_native_json?.[chIdx] || [])
    : (run?.predictions_json?.[chIdx] || []);
  const baselineFull = baselineChIdx >= 0 ? (run?.baseline_json?.values?.[baselineChIdx] || []) : [];
  const baselineSeries = (useNative && nativeAvailable && run?.native_interval_minutes)
    ? baselineFull.filter((_, i) => i % Math.max(1, Math.round(run.native_interval_minutes)) === 0)
    : baselineFull;

  return (
    <div style={{ fontFamily: "Inter, sans-serif", color: C.text }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        select, input, button { background: ${C.surfaceAlt}; border: 1px solid ${C.border}; color: ${C.text}; border-radius: 6px; padding: 7px 10px; font-size: 13px; font-family: inherit; }
        button { cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
        button:hover:not(:disabled) { border-color: ${C.accent}; color: ${C.accent}; }
        button:disabled { opacity: 0.4; cursor: not-allowed; }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th, td { padding: 6px 8px; text-align: left; }
        th { font-size: 11px; font-weight: 500; color: ${C.textSub}; border-bottom: 1px solid ${C.border}; }
        td { border-bottom: 1px solid ${C.border}; }
      `}</style>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <p style={{ fontSize: 14, fontWeight: 600 }}>📍 Scheduled locations</p>
          <button onClick={loadSchedules} disabled={loadingSchedules}>
            {loadingSchedules ? <><Spinner /> Refreshing…</> : "↻ Refresh"}
          </button>
        </div>
        <Err msg={schedulesError} />

        {schedules.length === 0 && !loadingSchedules ? (
          <p style={{ fontSize: 12, color: C.textSub }}>
            No active schedules yet — set one up from the "Automatic inference for this location"
            step in the Inference tab after activating a station and picking a weight set.
          </p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px,1fr))", gap: 10 }}>
            {schedules.map(s => (
              <button
                key={s.device_id}
                onClick={() => setSelectedDevice(s.device_id)}
                style={{
                  textAlign: "left", padding: "10px 12px", flexDirection: "column", alignItems: "flex-start", gap: 4,
                  ...(selectedDevice === s.device_id ? { borderColor: C.accent, color: C.accent } : {}),
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 13 }}>{s.location_name || s.device_id}</span>
                <span style={{ fontSize: 11, color: C.textSub }}>
                  {pill(s.source?.toUpperCase())} · every {s.frequency_minutes}min · {s.weights_id}
                </span>
                {s.last_error && <span style={{ fontSize: 10, color: C.danger }}>⚠ {s.last_error.slice(0, 60)}</span>}
              </button>
            ))}
          </div>
        )}
      </Card>

      {selectedDevice && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <p style={{ fontSize: 14, fontWeight: 600 }}>
              Run history — <code style={{ color: C.accent, fontSize: 12 }}>{selectedDevice}</code>
            </p>
            <button onClick={() => loadPredictions(selectedDevice)} disabled={loadingPredictions}>
              {loadingPredictions ? <><Spinner /> Loading…</> : "↻ Refresh"}
            </button>
          </div>
          <Err msg={predictionsError} />

          {loadingPredictions ? (
            <p style={{ fontSize: 12, color: C.textSub, display: "flex", alignItems: "center", gap: 8 }}>
              <Spinner /> Loading prediction history…
            </p>
          ) : predictions.length === 0 ? (
            <p style={{ fontSize: 12, color: C.textSub }}>
              No runs yet for this schedule — inference_runner.service checks every 60s and runs
              when a schedule is due. Check back shortly, or verify the service is running.
            </p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead><tr><th></th><th>Run at</th><th>Weights</th><th>Native interval</th><th>Horizon</th></tr></thead>
                <tbody>
                  {predictions.map((p, i) => (
                    <tr key={p.id} style={{ background: i === selectedRunIdx ? C.accentDim : "transparent", cursor: "pointer" }}
                        onClick={() => setSelectedRunIdx(i)}>
                      <td>{i === selectedRunIdx ? "▶" : ""}</td>
                      <td style={{ fontFamily: "monospace" }}>{p.run_at?.slice(0, 16).replace("T", " ")}</td>
                      <td style={{ fontSize: 11, color: C.textSub }}>{p.weights_id}</td>
                      <td>{p.native_interval_minutes != null ? `~${p.native_interval_minutes} min` : "—"}</td>
                      <td>{p.forecast_horizon_minutes} min</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {run && (
        <Card>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>📈 Model forecast vs raw NWP forecast</p>
          {run.channels_json?.length !== run.predictions_json?.length && (
            <p style={{ fontSize: 11, color: C.warn, marginBottom: 10 }}>
              ⚠ This run's stored channel list ({run.channels_json?.length ?? 0}) doesn't match its
              prediction row count ({run.predictions_json?.length ?? 0}) — it was likely produced
              before the channel-labeling fix, so some series in this dropdown may be mislabeled.
            </p>
          )}
          {run.native_interval_minutes != null && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <p style={{ fontSize: 11, color: C.textSub }}>
                Station reports every ~{run.native_interval_minutes} min.
              </p>
              <label style={{ fontSize: 11, color: C.textSub, display: "flex", alignItems: "center", gap: 4 }}>
                <input type="checkbox" checked={useNative} onChange={e => setUseNative(e.target.checked)} />
                Plot at native cadence (recommended)
              </label>
            </div>
          )}

          <div style={{ marginBottom: 10, maxWidth: 260 }}>
            <Label>Channel</Label>
            <select value={chan || ""} onChange={e => setChannel(e.target.value)} style={{ width: "100%" }}>
              {comparableChannels.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <MiniLineChart
            seriesA={modelSeries} seriesB={baselineSeries}
            labelA="Model forecast" labelB="NWP forecast"
            colorA={C.success} colorB={C.accent}
          />
        </Card>
      )}
    </div>
  );
}